import React from 'react'
import { useHistory } from 'react-router-dom'
import { graphql, useFragment } from 'react-relay/hooks'
import { keyBy } from 'lodash'
import { useParams } from 'react-router-dom'
import { FormattedDate } from 'react-intl'
import { useAuth } from 'hooks'
import { FlexRow, Icon, Table as Tables, Text, Tooltip } from 'elements'
import CompanyContextMenu from './CompanyContextMenu'
import SeverityCircle from '../../common/SeverityCircle'
import { toDateTime } from 'utils/date'

const { Row, Cell } = Tables.Card

const getIconProps = (isArchived, isCurrentCompany) => {
  const baseProps = { size: 'medium', mr: 'xlarge' }

  if (isArchived) {
    return { ...baseProps, name: 'trash', color: 'foreground.0' }
  } else if (isCurrentCompany) {
    return { ...baseProps, name: 'arrow-circle-right', color: 'secondary.0' }
  }
  return { ...baseProps, name: 'globe-alt', color: 'foreground.0' }
}

const ReportMetricsCells = ({ reportMetrics, cellProps }) => {
  const keyedMetrics = keyBy(reportMetrics, 'name')

  return (
    <>
      <Cell {...cellProps}>
        <FlexRow alignItems="center">
          <SeverityCircle severity={2} styleProps={{ boxProps: { mr: 'small' } }} />
          <Text>{keyedMetrics?.anomalyCountHigh?.value ?? '--'}</Text>
        </FlexRow>
      </Cell>
      <Cell {...cellProps}>
        <FlexRow alignItems="center">
          <SeverityCircle severity={1} styleProps={{ boxProps: { mr: 'small' } }} />
          <Text>{keyedMetrics?.anomalyCountMedium?.value ?? '--'}</Text>
        </FlexRow>
      </Cell>
      <Cell {...cellProps}>
        <FlexRow alignItems="center">
          <SeverityCircle severity={0} styleProps={{ boxProps: { mr: 'small' } }} />
          <Text>{keyedMetrics?.anomalyCountLow?.value ?? '--'}</Text>
        </FlexRow>
      </Cell>
    </>
  )
}

export default function CompaniesTableRow({ company, disabled, isLast }) {
  const { currentCompany, userIsCurrentOrgAdmin } = useAuth()
  const { orgSlug } = useParams()
  const history = useHistory()
  const rowCompany = useFragment(
    graphql`
      fragment CompaniesTableRow_company on Company {
        id
        rowId
        name
        legalName
        createdAt
        deletedAt
        reports {
          totalCount
        }
        reportMetrics {
          name
          value
        }
        organization {
          organizationSubscriptions {
            nodes {
              currentPeriodEnd
              billingPlan {
                slug
              }
            }
          }
        }
      }
    `,
    company,
  )

  const companyPath = `/orgs/${orgSlug}/companies/${rowCompany.id}/reports`
  const isCurrentCompany = currentCompany?.id === rowCompany.id
  const isArchived = !!rowCompany.deletedAt
  const { reportMetrics } = rowCompany

  const orgSubscription = rowCompany.organization.organizationSubscriptions?.nodes?.[0]
  const currentPeriodEnd = orgSubscription?.currentPeriodEnd
  const teamPlan = orgSubscription?.billingPlan?.slug === 'team-monthly'

  let displayBillingNotice = false

  // the last client in the table is your free client
  if (teamPlan && currentPeriodEnd && !isLast && !isArchived) {
    const lastPeriodEndDt = toDateTime(currentPeriodEnd).minus({ months: 1 })

    displayBillingNotice = toDateTime(rowCompany.createdAt) > lastPeriodEndDt
  }

  const rowProps = {
    interactive: !disabled,
    onClick: () => !disabled && history.push(companyPath),
  }

  const cellProps = {
    kind: isArchived || disabled ? 'inactive' : undefined,
  }

  return (
    <Row {...rowProps}>
      <Cell {...cellProps}>
        <FlexRow alignItems="center">
          <Icon {...getIconProps(isArchived, isCurrentCompany)} />
          <Text
            fontSize="large"
            fontWeight="bold"
            truncate
            title={rowCompany.name}
            lineHeight="large"
          >
            {rowCompany.name}
          </Text>
          {displayBillingNotice && (
            <>
              <Icon
                ml="medium"
                size="medium"
                name="exclamation-circle"
                color="neutral.3"
                data-tip
                data-for={rowCompany.rowId + '-tooltip'}
              />
              <Tooltip id={rowCompany.rowId + '-tooltip'} role="tooltip" place="bottom">
                <Text
                  ml="medium"
                  fontSize="medium"
                  lineHeight="large"
                  color="neutral.3"
                  fontStyle="italic"
                >
                  This client will be added to your bill on{' '}
                  <FormattedDate value={currentPeriodEnd} />
                </Text>
              </Tooltip>
            </>
          )}
        </FlexRow>
      </Cell>
      <ReportMetricsCells reportMetrics={reportMetrics} cellProps={cellProps} />
      <Cell {...cellProps}>
        <Text color="neutral.0" whiteSpace="nowrap" title={rowCompany.createdAt}>
          <FormattedDate value={rowCompany.createdAt} />
        </Text>
      </Cell>
      <Cell {...cellProps}>
        <Text color="neutral.0" whiteSpace="nowrap" title={rowCompany.createdAt}>
          {rowCompany.reports.totalCount} report{rowCompany.reports.totalCount === 1 ? '' : 's'}
        </Text>
      </Cell>
      <Cell {...cellProps}>
        <FlexRow justifyContent="center">
          {userIsCurrentOrgAdmin ? (
            <CompanyContextMenu company={company} />
          ) : (
            <Icon name="angle-right" size="large" color="neutral.2" />
          )}
        </FlexRow>
      </Cell>
    </Row>
  )
}
